import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ToggleButton, ToggleButtonGroup } from '@heroui/react'

import { ModulePanel, ModuleShell } from '@/components/layout'
import {
  AdjustmentsPanel,
  CountsPanel,
  LocationFilter,
  LocationFilters,
  LocationsPanel,
  RecipeModifiersPanel,
  RecipesPanel,
  StockOverviewPanel,
  StockItemType,
  StockItemsPanel,
  UnitsPanel,
} from '@/features/inventory'
import type { InventoryLocationFilterValues, StockItemKind } from '@/features/inventory'

import {
  InventoryTab,
  InventoryTabGroup,
  groupOf,
  inventoryTabGroups,
  inventoryTabPaths,
} from './inventory-tabs'

const NO_LOCATION_FILTERS: InventoryLocationFilterValues = { type: null, status: null }

/** The Recipes tab's query param naming the item whose recipe to pick out. */
const RECIPE_ITEM_PARAM = 'item'

/**
 * The Stock Items tab's query param naming the list — `?category=ingredient`.
 * `category` because it is the API's own name for the item type.
 */
const STOCK_KIND_PARAM = 'category'

/** Each list as the URL spells it: lower case, as a person would type it. */
const stockKindParams: Record<StockItemKind, string> = {
  [StockItemType.Product]: 'product',
  [StockItemType.Ingredient]: 'ingredient',
}

/** Products unless the URL names ingredients — a missing or mistyped value included. */
function stockKindOf(value: string | null): StockItemKind {
  return value === stockKindParams[StockItemType.Ingredient]
    ? StockItemType.Ingredient
    : StockItemType.Product
}

/** The toggle's label for each section that sits under a grouped tab. */
const sectionLabels: Partial<Record<InventoryTab, string>> = {
  [InventoryTab.StockItems]: 'Stock items',
  [InventoryTab.Recipes]: 'Recipes',
  [InventoryTab.Modifiers]: 'Modifiers',
  [InventoryTab.Units]: 'Units',
  [InventoryTab.Locations]: 'Locations',
  [InventoryTab.Counts]: 'Counts',
  [InventoryTab.Adjustments]: 'Adjustments',
}

const groupLabels: Record<InventoryTabGroup, string> = {
  [InventoryTabGroup.Setup]: 'Setup',
  [InventoryTabGroup.Movement]: 'Movement',
}

interface SectionSwitchProps {
  group: InventoryTabGroup
  value: InventoryTab
  onChange: (tab: InventoryTab) => void
}

/**
 * Which section a grouped tab shows. Segmented, like Overview's own toggle; a
 * group of one has nothing to switch between, so it shows nothing.
 */
function SectionSwitch({ group, value, onChange }: SectionSwitchProps) {
  const sections = inventoryTabGroups[group]
  if (sections.length < 2) return null

  return (
    <ToggleButtonGroup
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={new Set([value])}
      onSelectionChange={(keys) => {
        const [first] = keys as Set<string>
        if (first) onChange(first as InventoryTab)
      }}
      aria-label={`${groupLabels[group]} section`}
      className="self-start"
    >
      {sections.map((tab) => (
        <ToggleButton
          key={tab}
          id={tab}
          size="sm"
          className="data-[selected]:bg-background data-[selected]:text-foreground px-4 text-xs data-[selected]:shadow-sm"
        >
          {sectionLabels[tab]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

interface InventoryPageProps {
  /** The tab its route names — see `inventoryTabPaths`. */
  tab: InventoryTab
}

/**
 * Inventory, on one screen: what is counted, the counts and write-offs that
 * correct it, how it is made, the choices that change it, what it is measured
 * in, and where it is held.
 *
 * Unlike Products, the tab lives in the URL rather than in state: switching tabs
 * navigates, so a link or a reload opens the tab it named and the back button
 * steps back through them. Every tab route renders this same page, so the bar's
 * choices — the location, the recipe — survive moving between tabs.
 *
 * Each tab's body is a panel in the inventory feature, and the bar carries only
 * the visible tab's scope: whose stock, which kinds of location.
 *
 * Stock Items, Recipes, Modifiers, Units and Locations share the Setup tab;
 * Counts and Adjustments share the Movement tab. Each grouped tab has a toggle at
 * the top of it. Each section keeps its own route, so the toggle navigates like a
 * tab does.
 *
 * An item's Recipe button on Stock Items navigates to the Recipes tab with
 * `?item=<id>`, which picks that item's recipe out — in the URL, like the tab, so
 * a reload keeps it and the back button returns to the list it came from.
 *
 * Which list Stock Items shows — products or ingredients — is in the URL too, as
 * `?category=`, for the same reasons.
 */
export function InventoryPage({ tab }: InventoryPageProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const recipeItemId = tab === InventoryTab.Recipes ? searchParams.get(RECIPE_ITEM_PARAM) : null
  const stockKind = stockKindOf(searchParams.get(STOCK_KIND_PARAM))

  /* Replaces the entry rather than adding one: flipping between the two lists is
     a filter, and the back button should leave the tab, not undo each flip. */
  function changeStockKind(next: StockItemKind) {
    setSearchParams(
      (params) => {
        params.set(STOCK_KIND_PARAM, stockKindParams[next])
        return params
      },
      { replace: true },
    )
  }

  /**
   * Whose stock Overview and Stock Items show — `null` for every location. One
   * choice for both, so moving between them keeps the place in view.
   */
  const [locationId, setLocationId] = useState<string | null>(null)
  /** The recipe whose modifiers show — `null` for the first one, until a modifier is added to another. */
  const [recipeId, setRecipeId] = useState<string | null>(null)
  const [locationFilters, setLocationFilters] =
    useState<InventoryLocationFilterValues>(NO_LOCATION_FILTERS)

  const group = groupOf(tab)
  const goTo = (next: InventoryTab) => navigate(inventoryTabPaths[next])

  return (
    <ModuleShell
      tabs={[
        { id: InventoryTab.Overview, label: 'Overview' },
        { id: InventoryTabGroup.Setup, label: groupLabels[InventoryTabGroup.Setup] },
        { id: InventoryTabGroup.Movement, label: groupLabels[InventoryTabGroup.Movement] },
      ]}
      selectedKey={group ?? tab}
      onSelectionChange={(key) => {
        // A grouped tab opens on its first section.
        const grouped = inventoryTabGroups[key as InventoryTabGroup]
        goTo(grouped ? grouped[0] : (key as InventoryTab))
      }}
      toolbar={
        tab === InventoryTab.StockItems || tab === InventoryTab.Overview ? (
          <LocationFilter value={locationId} onChange={setLocationId} />
        ) : tab === InventoryTab.Locations ? (
          <LocationFilters value={locationFilters} onChange={setLocationFilters} />
        ) : null
      }
    >
      <ModulePanel id={InventoryTab.Overview}>
        <StockOverviewPanel locationId={locationId} />
      </ModulePanel>

      <ModulePanel id={InventoryTabGroup.Setup}>
        {group === InventoryTabGroup.Setup && (
          <SectionSwitch group={group} value={tab} onChange={goTo} />
        )}

        {tab === InventoryTab.Recipes ? (
          <RecipesPanel
            stockItemId={recipeItemId}
            onShowAll={() => navigate(inventoryTabPaths[InventoryTab.Recipes])}
          />
        ) : tab === InventoryTab.Modifiers ? (
          <RecipeModifiersPanel recipeId={recipeId} onRecipeChange={setRecipeId} />
        ) : tab === InventoryTab.Units ? (
          <UnitsPanel />
        ) : tab === InventoryTab.Locations ? (
          <LocationsPanel
            filters={locationFilters}
            onClearFilters={() => setLocationFilters(NO_LOCATION_FILTERS)}
          />
        ) : (
          <StockItemsPanel
            locationId={locationId}
            kind={stockKind}
            onKindChange={changeStockKind}
            onShowRecipe={(item) =>
              navigate({
                pathname: inventoryTabPaths[InventoryTab.Recipes],
                search: new URLSearchParams({ [RECIPE_ITEM_PARAM]: item.id }).toString(),
              })
            }
          />
        )}
      </ModulePanel>

      <ModulePanel id={InventoryTabGroup.Movement}>
        {group === InventoryTabGroup.Movement && (
          <SectionSwitch group={group} value={tab} onChange={goTo} />
        )}

        {tab === InventoryTab.Adjustments ? <AdjustmentsPanel /> : <CountsPanel />}
      </ModulePanel>
    </ModuleShell>
  )
}
